import { Button, Checkbox } from 'antd';
import moment, { Moment } from 'moment';
import { pipe } from 'ramda';
import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { LockSettingConfiguration } from 'api/timesheets/configuration/configuration';
import { ReactComponent as FirewallIcon } from 'assets/svgs/firewall_classic.svg';
import { CustomDueDatePicker } from 'components/atoms/due-date-picker';
import { Modal } from 'components/atoms/modal';
import { getWeekStartDate } from 'components/organisms/timesheets/common/utils';
import { map, orElse } from 'core/maybe';
import { maybeValueOf } from 'core/state';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { localeData } from 'translations/en-moment';
import { getDays } from 'utils/date-field/get-days-array';
import { ControlPanel } from '../control-panel';
import { SetState, State } from '../state';

const StyledCheckbox = styled(Checkbox)`
  display: flex;
  width: fit-content;
  margin-left: 0;
  gap: var(--spacing-04);

  .ant-checkbox {
    position: relative;
    top: var(--spacing-01);
    align-self: start;
  }
`;

const LockTimesheetHeading = styled.div`
  font-weight: var(--heading-6-weight);
`;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    width: 70%;
  }
`;

const LockTimesheetWrapper = styled.div`
  display: flex;
  gap: calc(var(--spacing-04) + var(--spacing-03));
`;

const TimesheetLockIcon = styled(FirewallIcon)`
  min-width: var(--spacing-05);
  position: relative;
  top: var(--spacing-02);
`;

const SetNewLockDate = styled.div`
  margin-top: var(--spacing-04);
  color: var(--gray-100);
`;

const StyledDescription = styled.div`
  font-size: var(--font-size-base);
  color: var(--gray-60);
`;

const SetFreezeDate = styled(Button)`
  font-size: var(--font-size-base);
  color: var(--blue-60);
  padding-left: 0%;
  height: fit-content;
`;

const LockDate = styled.div`
  font-size: var(--font-size-base);
`;

const StyledDate = styled.span`
  font-weight: var(--heading-8-weight);
`;

const isSameCentury = (date: Moment) =>
  Math.floor(date.get('year') / 100) === Math.floor(moment().get('year') / 100);

export const humanise = (date: Moment): string =>
  date.format(
    `DD MMM${
      !moment().isSame(date, 'year')
        ? isSameCentury(date)
          ? ' YY'
          : ' YYYY'
        : ''
    }`,
  );

const DatePicker = ({
  onDateChange,
  freezeDate,
  diableDateAfterCurrentDate,
  triggerText,
}) => (
  <CustomDueDatePicker
    onDateChange={([_, due]) => onDateChange(due)}
    size="small"
    canChooseStartDate={false}
    renderTrigger={() => (
      <SetFreezeDate type="link">
        <FormattedMessage id={triggerText} />
      </SetFreezeDate>
    )}
    dueDate={freezeDate}
    disabledDate={diableDateAfterCurrentDate}
    picker="week"
  />
);

interface Props {
  configuration: State<LockSettingConfiguration>;
  onConfigurationChange: SetState<LockSettingConfiguration>;
}

export const LockTimeSettings: FC<Props> = ({
  configuration,
  onConfigurationChange,
}) => {
  const translate = useTranslate();
  const label = useTranslation('settings.timesheets.controls.lock_time.label');
  const description = useTranslation(
    'settings.timesheets.controls.lock_time.description',
  );
  const [freezeDate, setFreezeDate] = useState('');
  const [confirmationPopupVisible, setConfirmationPopupVisible] =
    useState(false);

  const diableDateAfterCurrentDate = (current) => {
    return current && current > moment().endOf('day');
  };
  const locale = 'en';

  useLayoutEffect(() => {
    moment.updateLocale(locale, { ...localeData, weekStart: 1 });

    return () => {
      moment.updateLocale(locale, { ...localeData, weekStart: 0 });
    };
  }, [locale]);

  const lockApprovalValue = useMemo(
    () =>
      pipe(
        () => configuration,
        maybeValueOf,
        map((config) => config.lock.enabled),
        orElse(false),
      )(),
    [configuration],
  );

  const onLockApprovalChange = useCallback(
    (next) => {
      if (configuration[1]) {
        onConfigurationChange({
          ...configuration[1],
          lock: { enabled: next.target.checked as boolean },
        });
      }
    },
    [configuration, onConfigurationChange],
  );

  const freezeTimesheetValue = useMemo(
    () =>
      pipe(
        () => configuration,
        maybeValueOf,
        map((config) => config.freeze.enabled),
        orElse(false),
      )(),
    [configuration],
  );

  const onFreezeTimesheet = useCallback(() => {
    if (configuration[1]) {
      onConfigurationChange({
        ...configuration[1],
        freeze: { enabled: true, enableAfter: freezeDate },
      });
    }
  }, [configuration, freezeDate, onConfigurationChange]);

  useEffect(() => {
    if (freezeTimesheetValue && configuration[1]) {
      const date = pipe(
        () => configuration,
        maybeValueOf,
        map((config) => config.freeze.enableAfter ?? ''),
        orElse(''),
      )();

      setFreezeDate(date);
    }
  }, [configuration, freezeTimesheetValue]);

  const onSetDateCancel = useCallback(() => {
    setConfirmationPopupVisible(false);
    if (freezeTimesheetValue && configuration[1]) {
      const date = pipe(
        () => configuration,
        maybeValueOf,
        map((config) => config.freeze.enableAfter ?? ''),
        orElse(''),
      )();

      setFreezeDate(date);
    }
  }, [configuration, freezeTimesheetValue]);

  const onDateChange = useCallback(
    (due) => {
      if (due && due !== freezeDate) {
        const currentWeekDays = getDays(
          getWeekStartDate(due?.toString()),
          7,
        ).map((x) => x.format('YYYY-MM-DD'));

        if (due.toString() < currentWeekDays[0]) {
          setFreezeDate(due.toString());
        } else {
          setFreezeDate(currentWeekDays[currentWeekDays.length - 1]);
        }

        setConfirmationPopupVisible(true);
      }
    },
    [freezeDate],
  );

  return (
    <ControlPanel label={label} descriptions={[description]}>
      <StyledCheckbox
        onChange={onLockApprovalChange}
        checked={lockApprovalValue}
      >
        <div>
          <FormattedMessage id="settings.timesheets.controls.lock_time.once_approved.label" />
        </div>
        <StyledDescription>
          <FormattedMessage id="settings.timesheets.controls.lock_time.once_approved.description" />
        </StyledDescription>
      </StyledCheckbox>
      <LockTimesheetWrapper>
        <TimesheetLockIcon />
        <div>
          <LockTimesheetHeading>
            <FormattedMessage id="settings.timesheets.controls.lock_time.freeze_timesheets.label" />
          </LockTimesheetHeading>
          <StyledDescription>
            <FormattedMessage id="settings.timesheets.controls.lock_time.freeze_timesheets.description" />
          </StyledDescription>
          {freezeTimesheetValue ? (
            <SetNewLockDate>
              <LockDate>
                <FormattedMessage id="settings.timesheets.controls.lock_time.freeze_timesheets.lock_date" />
                <StyledDate>{humanise(moment(freezeDate))}</StyledDate>
              </LockDate>
              <DatePicker
                onDateChange={onDateChange}
                freezeDate={freezeDate}
                diableDateAfterCurrentDate={diableDateAfterCurrentDate}
                triggerText="settings.timesheets.controls.lock_time.freeze_timesheets.set_new_lock_date"
              />
            </SetNewLockDate>
          ) : (
            <DatePicker
              onDateChange={onDateChange}
              freezeDate={freezeDate}
              diableDateAfterCurrentDate={diableDateAfterCurrentDate}
              triggerText="settings.timesheets.controls.lock_time.freeze_timesheets.set_date"
            />
          )}
          <StyledModal
            visible={confirmationPopupVisible}
            title={
              <FormattedMessage id="settings.timesheets.controls.lock_time.freeze_timesheets.modal.title" />
            }
            confirmLoading={false}
            okText={
              <FormattedMessage id="settings.timesheets.controls.lock_time.freeze_timesheets.modal.submit_text" />
            }
            onCancel={onSetDateCancel}
            onOk={() => {
              setConfirmationPopupVisible(false);
              onFreezeTimesheet();
            }}
          >
            <div>
              {translate(
                'settings.timesheets.controls.lock_time.freeze_timesheets.modal.decription',
              )}
            </div>
            <br />
            <div>
              {translate(
                'settings.timesheets.controls.lock_time.freeze_timesheets.modal.decription_two',
                {
                  date: humanise(moment(freezeDate)),
                  strong: (chunks) => <strong>{chunks}</strong>,
                },
              )}
            </div>
          </StyledModal>
        </div>
      </LockTimesheetWrapper>
    </ControlPanel>
  );
};
